import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setFabrics,
  setTotal,
  setPage,
  setSize,
  setSearch,
  setFilterType,
  setFabricCategories,
  setMetrics,
  setShowForm,
  setFabricDetails,
  loadEditingFabric,
  resetForm,
  clearPendingFabricEdit,
  exitProjectDataMode,
} from '../../../store/dataFabricSlice';
import {
  enterFolderEditFromProject,
  enterFolderCreateFromProject,
} from '../../../store/unstructuredThreadSlice';
import { setActiveTabId, switchFabric, invalidateFabricTemplateCache } from '../../../store/genbiSlice';
import { TopNav, SideNav } from '../navigation/ConsoleNavigation';
import axios from 'axios';
import DataFabricList from './DataFabricList';
import DataFabricForm from './DataFabricForm';
import DataFabricDetails from './DataFabricDetails';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import Loader from '../../utils/loading/Loader';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import { usePermissions } from '../../utils/PermissionsContext';
import { PERMISSIONS } from '../../utils/permissionRoutes';

const EMPTY_CATEGORY_FILTER = { indication: [], intervention: [] };

const DataFabricManagement = () => {
  const dispatch = useDispatch();

  // ── Redux-persisted state (survives page navigation) ──────────────────────
  const {
    fabrics,
    total,
    page,
    size,
    search,
    filterType,
    fabricCategories,
    metrics,
    showForm,
    editingFabric,
    fabricDetails,
    fromProject,
    pendingFabricEditId,
    returnToTemplateId,
  } = useSelector((state) => state.dataFabric);

  // Used only to confirm the originating template tab is still open before
  // making it active again on the way back.
  const { openTemplateTabs } = useSelector((state) => state.genbi);

  // Local state for the filter dropdown. category is now bucketed
  // ({ indication: [...], disease: [...] }); country is new. Neither is
  // persisted to redux — they're re-fetched fresh alongside the fabric list,
  // same as the tag option list always has been.
  const [categoryFilter, setCategoryFilter] = useState(EMPTY_CATEGORY_FILTER);
  const [countryFilter, setCountryFilter] = useState([]);
  const [fabricCountries, setFabricCountries] = useState([]); // [{id, name, iso_code}] option list

  // The scroll lives on <main>, which stays mounted while list/details/form
  // swap inside it — so without this the list keeps whatever offset the
  // details or form view was left at and opens part-scrolled.
  const mainRef = useRef(null);
  const view = showForm ? 'form' : fabricDetails ? 'details' : 'list';
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [view]);

  // ── Local transient state (resets on mount — intentional) ─────────────────
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Routes this user actually holds, straight from the backend. Gating on this
  // rather than on platform_role keeps the rule in one place — checking
  // `role === 'ADMIN' || ...` here would be a second copy of the permission
  // model that drifts the moment a role's routes change.
  //
  // Used for the Create Project button, which has no project to be specific
  // about — the question is only whether this user may ever create one, and the
  // country check happens server-side once they pick one.
  //
  // Per-project actions do not use this. DataFabricDetails reads can_edit and
  // can_delete straight off get_fabric_details, which answers for that one
  // project using the same checks the endpoints run.
  const { can, refreshPermissions } = usePermissions();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [fabricToDelete, setFabricToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // BMS-specific: sidebar category filter — event-driven + sessionStorage, no persistence needed
  const [sidenavCategoryFilter, setSidenavCategoryFilter] = useState([]);

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const navigate = useNavigate();

  const API_BASE_URL = '/pceapi/fabrics';
  const headers = { Authorization: GetCookie('access_token') };

  // Reset form/details when component mounts (navigating back from other routes).
  // Skip reset when arriving from "Link Data" with pre-loaded fabric data.
  useEffect(() => {
    if (fromProject && editingFabric && showForm) return;
    dispatch(resetForm());
    dispatch(setFabricDetails(null));
  }, []);

  // Arriving from the HTA template view ("Manage Data") or returning from a
  // folder create/edit — open the project edit form straight away.
  // Runs after the reset effect above, so it is not clobbered by it.
  useEffect(() => {
    if (!pendingFabricEditId) return;
    openPendingFabricEdit(pendingFabricEditId);
  }, [pendingFabricEditId]);

  // Fetch fabrics on mount and when dependencies change.
  // Skipped when the project form was opened via "Link Data" — the list is not
  // rendered in that mode, so the call fed nothing.
  // NOTE: fromProject is deliberately NOT in the dep array. Adding it would fire
  // a wasted fetch at the moment we clear the flag and navigate away.
  useEffect(() => {
    if (fromProject) return;
    fetchFabrics();
  }, [page, size, search, refreshTrigger, filterType, categoryFilter, countryFilter]);

  // Fetch metrics on mount
  useEffect(() => {
    if (fromProject) return;
    fetchMetrics();
  }, []);
  
  useEffect(() => {
    if (fabricDetails?.fabric?.id) {
      fetchFabricDetails(fabricDetails.fabric.id);
    }
  }, []);

  // Listen for sidebar category change event (BMS-specific)
  useEffect(() => {
    const handleCategoryChange = (e) => {
      setSidenavCategoryFilter(e.detail ? [e.detail] : []);
      dispatch(resetForm());           // Close edit form
      dispatch(setFabricDetails(null)); // Close fabric details
    };
    const handleShowAll = () => {
      setSidenavCategoryFilter([]);
      dispatch(resetForm());           // Close edit form
      dispatch(setFabricDetails(null)); // Close fabric details
    };
    window.addEventListener('hta_category_change', handleCategoryChange);
    window.addEventListener('hta_show_all', handleShowAll);
    return () => {
      window.removeEventListener('hta_category_change', handleCategoryChange);
      window.removeEventListener('hta_show_all', handleShowAll);
    };
  }, []);

  // Read category from sessionStorage on mount — cross-route navigation (BMS-specific)
  useEffect(() => {
    const savedCategory = sessionStorage.getItem('hta_selected_category');
    if (savedCategory) {
      setSidenavCategoryFilter([savedCategory]);
      sessionStorage.removeItem('hta_selected_category');
    }
  }, []);

  // Fetch fabrics with pagination and search
  const fetchFabrics = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading Projects');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/list`,
        {
          page, size, search: search || undefined, filter_type: filterType,
          indication: categoryFilter.indication.length > 0 ? categoryFilter.indication : undefined,
          intervention: categoryFilter.intervention.length > 0 ? categoryFilter.intervention : undefined,
          country_id: countryFilter.length > 0 ? countryFilter : undefined,
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load projects');
        setIsErrorPopupVisible(true);
        return;
      }
      
      dispatch(setFabrics(data.data || []));
      dispatch(setTotal(data.total || 0));
      dispatch(setFabricCategories(data.categories || EMPTY_CATEGORY_FILTER));
      setFabricCountries(data.countries || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setPopupErrorMessage(error.message || 'Failed to load projects');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Fetch metrics
  const fetchMetrics = async () => {
    try {
      setIsInitialLoading(true);
      const response = await axios.get(`${API_BASE_URL}/metrics`, { headers });
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to load metrics');
        setIsErrorPopupVisible(true);
        return;
      }
      
      dispatch(setMetrics(data.data || {
        user_email: '',
        user_name: '',
        fabric_count: 0,
        thread_count: 0,
        team_count: 0,
      }));
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setPopupErrorMessage(error.message || 'Failed to load metrics');
      setIsErrorPopupVisible(true);
    }
    finally{
      setIsInitialLoading(false);
    }
  };

  // Fetch fabric details for viewing
  const fetchFabricDetails = async (fabricId) => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading project details');
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/details/${fabricId}`,
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load project details');
        setIsErrorPopupVisible(true);
        return;
      }
      
      dispatch(setFabricDetails(data.data));
    } catch (error) {
      console.error('Error fetching project details:', error);
      setPopupErrorMessage(error.message || 'Failed to load project details');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Open form for editing a fabric
  const handleOpenEditForm = (fabric) => {
    dispatch(loadEditingFabric(fabric));
  };

  // Fetch the full fabric record, then open the edit form for it.
  // Needed because the caller only carries a fabric id, while
  // loadEditingFabric expects the whole object.
  const openPendingFabricEdit = async (fabricId) => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading project');

    try {
      const response = await axios.get(`${API_BASE_URL}/details/${fabricId}`, { headers });
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to load project');
        setIsErrorPopupVisible(true);
        return;
      }

      if (data.data && data.data.fabric) {
        dispatch(loadEditingFabric(data.data.fabric));
      }
    } catch (error) {
      console.error('Error opening project edit form:', error);
      setPopupErrorMessage(error.message || 'Failed to load project');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      dispatch(clearPendingFabricEdit());
    }
  };

  // Jump to Data Manager to edit one data folder, remembering where to come back to.
  const handleEditFolderFromProject = (threadId) => {
    dispatch(enterFolderEditFromProject({
      fabricId: editingFabric?.id,
      threadId
    }));
    authNavigate('/data-manager', 'iam', navigate);
  };

  // Jump to Data Manager to create a new data folder.
  const handleCreateFolderFromProject = () => {
    dispatch(enterFolderCreateFromProject({
      fabricId: editingFabric?.id
    }));
    authNavigate('/data-manager', 'iam', navigate);
  };

  // Open fabric details view
  const handleOpenFabricDetails = (fabricId) => {
  dispatch(setShowForm(false));
  dispatch(setFabricDetails(null));
  fetchFabricDetails(fabricId);                  
};

  // Navigate to hta-dossier when card is clicked (BMS-specific)
  const handleCardClickToGenbi = (fabricId) => {
    sessionStorage.setItem('genbi_fabric_id', fabricId);
    // Save active tag filters so GenbiTemplateList shows only the tags the user filtered by
    sessionStorage.setItem('genbi_active_tags', JSON.stringify(categoryFilter));

    // Switch the store to the new project BEFORE navigating. sessionStorage is
    // only the message ("open project N"); selectedFabricId is the state, and
    // it deliberately survives navigation. Leaving the two out of sync here is
    // what used to cost a wasted request: GenbiDashboard mounts, passes the
    // PREVIOUS project's id down, GenbiTemplateList's effect fires first
    // (children flush before parents) and fetches list-templates for the old
    // project, and only then does the dashboard's mount effect reconcile from
    // sessionStorage — changing selectedFabricId and firing a second fetch for
    // the project actually asked for. Setting it up front means the dashboard's
    // effect finds nothing to reconcile and only one fetch ever happens.
    //
    // Dispatched bare, without a setSelectedFabricId(null) first: switchFabric
    // early-returns when the id is unchanged, so re-opening the SAME project
    // leaves openTemplateTabs alone and the user's tabs survive the round trip.
    // Nulling first would defeat that guard and clear them.
    dispatch(switchFabric(Number(fabricId)));

    authNavigate('/hta-dossier', 'iam', navigate);
  };

  // Close fabric details view
  const handleCloseFabricDetails = () => {
    dispatch(setFabricDetails(null));
  };
  const confirmDelete = (fabric) => {
  setFabricToDelete(fabric);
  setShowDeleteConfirm(true);
};

const handleDelete = async () => {
  setShowDeleteConfirm(false);
  if (!fabricToDelete) return;

  setIsProcessingPopupVisible(true);
  setProcessingMessage(`Deleting project "${fabricToDelete.name}"`);

  try {
    const response = await axios.delete(
      `${API_BASE_URL}/delete`,
      {
        headers,
        data: { fabric_id: fabricToDelete.id }
      }
    );

    const data = response.data;

    if (data.status === 'NotAuthorized') {
      setIsProcessingPopupVisible(false);
      authNavigate('/login', 'iam', navigate);
      return;
    }

    if (data.status === 'failed') {
      setIsProcessingPopupVisible(false);
      setPopupErrorMessage(data.message || 'Failed to delete project');
      setIsErrorPopupVisible(true);
      return;
    }

    window.dispatchEvent(new Event('category_updated'));
    dispatch(setFabricDetails(null));
    setPopupSuccessMessage(data.message || 'Project deleted successfully');
    setIsSuccessPopupVisible(true);
    setRefreshTrigger((prev) => prev + 1);
  } catch (error) {
    console.error('Error deleting project:', error);
    setPopupErrorMessage(error.message || 'Failed to delete project');
    setIsErrorPopupVisible(true);
  } finally {
    setIsProcessingPopupVisible(false);
    setFabricToDelete(null);
  }
};
  // Return to the HTA template tab the user came from via "Manage Data".
  // The genbi slice keeps openTemplateTabs/activeTabId across routes, so a plain
  // navigate restores the tab. Deliberately NOT setting sessionStorage
  // 'genbi_fabric_id' here — that would trigger switchFabric and clear the tabs.
  const returnToTemplateView = () => {
    const templateId = returnToTemplateId;

    if (templateId && openTemplateTabs.some((tab) => tab.id === templateId)) {
      dispatch(setActiveTabId(templateId));
    }

    // Navigate first — GenbiDashboard's mount effect handles exitProjectDataMode cleanup.
    // Dispatching it here would trigger a re-render that briefly flashes the dashboard.
    authNavigate('/hta-dossier', 'iam', navigate);
  };

  // Handle form submission success
  const handleFormSuccess = (message) => {
    // An update may have unlinked data folders, which deletes agent-document
    // mappings server-side — drop this project's cached presets so they refetch.
    if (editingFabric?.id) {
      dispatch(invalidateFabricTemplateCache(editingFabric.id));
    }
    setPopupSuccessMessage(message);
    setIsSuccessPopupVisible(true);
    dispatch(resetForm());
    setRefreshTrigger((prev) => prev + 1);

    // Came from a project — go back to its template tab once the popup is seen
    if (fromProject) {
      setTimeout(() => returnToTemplateView(), 1200);
      return;
    }

    dispatch(exitProjectDataMode());
  };

  // Closing the project form ends the "came from project" context
  const handleFormCancel = () => {
    if (fromProject) {
      returnToTemplateView();
      return;
    }

    dispatch(resetForm());
    dispatch(exitProjectDataMode());
    // Refetch — the project may have been changed/deleted by another user meanwhile
    setRefreshTrigger((prev) => prev + 1);
  };

  // Wrapper setters for child props (dispatch wrappers)
  const handleSetPage = (val) => dispatch(setPage(val));
  const handleSetSize = (val) => dispatch(setSize(val));
  const handleSetSearch = (val) => dispatch(setSearch(val));
  const handleSetFilterType = (val) => dispatch(setFilterType(val));

  // Ignore updates that don't change the selected tags. DataFabricList syncs
  // from the sidebar on mount with a fresh object — without this guard the
  // new reference re-fires the list fetch effect (duplicate /list call).
  const sameCategoryFilter = (a, b) =>
    a.indication.length === b.indication.length &&
    a.indication.every((tag, i) => tag === b.indication[i]) &&
    a.intervention.length === b.intervention.length &&
    a.intervention.every((tag, i) => tag === b.intervention[i]);

  const handleSetCategoryFilter = (val) => {
    const next = val || EMPTY_CATEGORY_FILTER;
    if (sameCategoryFilter(next, categoryFilter)) return;
    setCategoryFilter(next);
    dispatch(setPage(1));
  };

  const sameCountryFilter = (a, b) =>
    a.length === b.length && a.every((id, i) => id === b[i]);

  const handleSetCountryFilter = (val) => {
    const next = val || [];
    if (sameCountryFilter(next, countryFilter)) return;
    setCountryFilter(next);
    dispatch(setPage(1));
  };

  const closeErrorPopup = () => setIsErrorPopupVisible(false);
  const closeSuccessPopup = () => setIsSuccessPopupVisible(false);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  /*
  if (isInitialLoading) {
    return (
      <div className="main-window">
        <TopNav />
        <div className="container-fluid">
          <div className="row">
            <SideNav />
            <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div><Loader/></div>
            </main>
          </div>
        </div>
      </div>
    );
  }
    */
  return (
    <div className="main-window">
      <TopNav />
      <div className="container-fluid">
        <div className="row">
          <SideNav />
          <main ref={mainRef} className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="col-10 m-auto pt-5 pb-5"> 
              <div className="margin-top-2 margin-bottom-2">
                <p className="ev-small-p-blue ev-bold-reg ev-fs-14">
                  {getGreeting()},
                  <span 
                    className="ev-bold-med avatar-tooltip ms-1"
                    data-tooltip={metrics.user_email}
                  >
                    {metrics.user_name}
                  </span>
                </p>
              </div>
              
              {/* Data Fabrics Section */}
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-4 mt-4">
                <p className="ev-med-p-blue mb-1">
                  Projects{' '}
                  {!showForm && !fabricDetails && <span className="ev-light-reg">({total})</span>}
                </p>
                {!showForm && !fabricDetails && can(PERMISSIONS.FABRIC_CREATE) && (
                  <button
                    className="ev-btn-yellow-fill-round ev-bold-reg"
                    onClick={() => {
                      dispatch(resetForm());
                      dispatch(setShowForm(true));
                    }}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Create Project
                  </button>
                )}
              </div>
              {/* <p className="ev-message-card2 mb-4">
                Orchestrate your data ecosystem, transforming Data Chaos into powerful business decisions.
              </p> */}
              
              {/* Show form, fabric details, or fabric list based on state */}
              {showForm ? (
                <DataFabricForm
                  editingFabric={editingFabric}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                  f_cats={fabricCategories}
                  fromProject={fromProject}
                  onEditFolderFromProject={handleEditFolderFromProject}
                  onCreateFolderFromProject={handleCreateFolderFromProject}
                />
              ) : fabricDetails ? (
                <DataFabricDetails
                  fabricDetails={fabricDetails}
                  onClose={() => {
                    handleCloseFabricDetails();
                    // Refetch — the project may have been deleted (here or by another user)
                    setRefreshTrigger((prev) => prev + 1);
                  }}
                  onRefresh={() => fetchFabricDetails(fabricDetails.fabric.id)}
                  onEditFabric=
                  {(fabric) => 
                    {
                      handleCloseFabricDetails();
                      handleOpenEditForm(fabric);
                    }
                  }
                />
              ) : (
                <DataFabricList
                  fabrics={fabrics}
                  total={total}
                  page={page}
                  setPage={handleSetPage}
                  size={size}
                  setSize={handleSetSize}
                  search={search}
                  setSearch={handleSetSearch}
                  filterType={filterType}
                  fabricCategories={fabricCategories}
                  setFilterType={handleSetFilterType}
                  countries={fabricCountries}
                  countryFilter={countryFilter}
                  setCountryFilter={handleSetCountryFilter}
                  categoryFilter={categoryFilter}
                  setCategoryFilter={handleSetCategoryFilter}
                  externalCategoryFilter={sidenavCategoryFilter}
                  onRefresh={() => {
                    setRefreshTrigger((prev) => prev + 1);
                    refreshPermissions();
                  }}
                  onEditFabric={handleOpenEditForm}
                  onViewFabricDetails={handleOpenFabricDetails}
                  onCardClick={handleCardClickToGenbi}
                />
              )}
            </div>
          </main>
        </div>
      </div>
      
      {/* Popups */}
      {isProcessingPopupVisible && <ProcessingPopup message={processingMessage} />}
      {isErrorPopupVisible && <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />}
      {isSuccessPopupVisible && <SuccessPopup onClose={closeSuccessPopup} message={popupSuccessMessage} />}
    </div>
  );
};

export default DataFabricManagement;